import { DAY_TRACK_PX, TODAY_TRACK_PX } from './rowMetrics.js';
import { cx } from '../primitives/cx.js';

export type DayPoint = {
  /** 0–1, or null for a day that cannot be plotted — a rest day, or today. */
  ratio: number | null;
  label: string;
};

export type DayLineProps = {
  /** Oldest → today, the same order and length as the grid's columns. */
  points: readonly DayPoint[];
  label: string;
  height?: number;
  className?: string;
};

/**
 * How each day went, drawn directly above the columns it describes.
 *
 * Geometry comes from `rowMetrics` — the same constants the strip and the date
 * header use — so the line sits over its own column by construction rather
 * than by two files agreeing about 28 pixels. It is meant to live INSIDE the
 * chart's scroll container so it moves with the days.
 *
 * A day with nothing promised leaves a GAP rather than plotting zero. Zero is
 * the shape of failure, and a rest day is not one; a line that dives every
 * Sunday describes the schedule, not the student. Today is unplotted for the
 * same reason — the day is not over, so it has no honest value yet.
 */
export function DayLine({ points, label, height = 40, className }: DayLineProps) {
  if (points.length === 0) return null;

  const pad = 4;
  const width = (points.length - 1) * DAY_TRACK_PX + TODAY_TRACK_PX;
  const plot = height - pad * 2;

  // Column centres, matching the tracks below: every past day is one narrow
  // track, and today is the wide one that holds the completion circle.
  const x = (index: number) =>
    index === points.length - 1
      ? index * DAY_TRACK_PX + TODAY_TRACK_PX / 2
      : index * DAY_TRACK_PX + DAY_TRACK_PX / 2;
  const y = (ratio: number) => pad + (1 - ratio) * plot;

  // Split at every gap so the line never draws through a day it has no value
  // for. Connecting across would invent a trend out of a rest day.
  const runs: { index: number; ratio: number }[][] = [];
  let run: { index: number; ratio: number }[] = [];
  for (const [index, point] of points.entries()) {
    if (point.ratio === null) {
      if (run.length > 0) runs.push(run);
      run = [];
    } else {
      run.push({ index, ratio: point.ratio });
    }
  }
  if (run.length > 0) runs.push(run);

  return (
    <svg
      role="img"
      aria-label={label}
      viewBox={`0 0 ${width} ${height}`}
      width={width}
      height={height}
      className={cx('block', className)}
    >
      {runs.map((segment, i) => {
        const line = segment.map((p) => `${x(p.index)},${y(p.ratio)}`).join(' ');
        const first = segment[0];
        const last = segment[segment.length - 1];

        return (
          <g key={i}>
            {/* Area under the line, in the soft accent — it gives the run some
                weight at 40px tall without adding a second colour. */}
            {segment.length > 1 && first && last && (
              <polygon
                points={`${x(first.index)},${height - pad} ${line} ${x(last.index)},${height - pad}`}
                fill="var(--aj-accent-soft)"
              />
            )}
            {segment.length > 1 ? (
              <polyline
                points={line}
                fill="none"
                stroke="var(--aj-accent)"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            ) : (
              // A lone day between two gaps still deserves to be on the chart.
              first && <circle cx={x(first.index)} cy={y(first.ratio)} r={2} fill="var(--aj-accent)" />
            )}
          </g>
        );
      })}

      {/* Hit targets, wider than any mark, so a day can be read rather than
          only glanced at. Unplottable days keep their label too — "nothing
          scheduled" is worth being able to check. */}
      {points.map((point, index) => (
        <rect
          key={index}
          x={x(index) - DAY_TRACK_PX / 2}
          y={0}
          width={DAY_TRACK_PX}
          height={height}
          fill="transparent"
        >
          <title>{point.label}</title>
        </rect>
      ))}
    </svg>
  );
}
